//Home WebGL layer tuning (which implementation mounts + the layout/progress
//constants the home timeline scene is authored against). Values are lifted
//verbatim from their old call sites — see the per-member comments.
import type { HomeWebGLType } from 'app/views/home/webgl/webgl.types';
import Config from 'config/settings';
import dev from 'config/dev';

//homeType resolution, highest priority first (each candidate validated
//against HomeWebGLType, invalid values fall through):
// 1. ?webgl=particles|particles-optimised|debug|shaders query (dev tool)
// 2. config/dev webglType, only while the /dev/home/ harness is active
// 3. default 'particles-optimised' (the SoA rewrite — flipped from
//    'particles' 2026-07-17 after the parity pass + benchmark:
//    docs/PARITY-particles-optimised.md, docs/PERF-PARTICLES.md. Instant
//    rollback / reference comparison stays one query param away:
//    ?webgl=particles is the original object-based scene, byte-untouched.)
const validHomeTypes: Array<HomeWebGLType> = ['particles', 'particles-optimised', 'debug', 'shaders'];
const homeTypeOverride = new URLSearchParams(location.search).get('webgl') as HomeWebGLType;

function resolveHomeType(): HomeWebGLType {
	if (validHomeTypes.indexOf(homeTypeOverride) !== -1) {
		return homeTypeOverride;
	}

	if (dev.active && dev.webglType && validHomeTypes.indexOf(dev.webglType) !== -1) {
		return dev.webglType;
	}

	return 'particles-optimised';
}

export default {
	//Which home WebGL implementation is mounted — see resolveHomeType above.
	homeType: resolveHomeType(),

	//Native design size the home scene was authored at (ex HomeTimeline._createSceneProps sizes.native).
	nativeSize: { w: 1920 * 4, h: 1080 },

	//Divisor for the timeline items' display scale, capped at 1 (ex HomeTimeline.resize scale divisor).
	layoutScaleBase: 1920 * 3,

	//Slide count the horizontal drag maps across (ex HomeTimeline._setContainerX).
	totalSlides: 4,

	//Drag-progress bands that select the active department scene; null between bands
	//(read by HomeZonesConfig.getParticleSceneFromProgress — was inlined there).
	sceneBands: {
		iconMax: 0.02, //'icon' while progress < this
		screens: { min: 0.23, max: 0.385 }, //'screens' inside this band (inclusive)
		spaces: { min: 0.615, max: 0.695 }, //'spaces' inside this band (inclusive)
		systemsMin: 0.9 //'systems' while progress > this
	},

	//Fixed-ratio content positions (px at native x4 sizing) per zone (ex TimelineZones._contentPositionsConfig).
	contentPositions: {
		box: { zone: 'spaces', cx: 0.5, cy: 0.36, widthPx: 540, heightPx: 400 },
		homeParticles: { zone: 'home', cx: 0.5, cy: 0.418, widthPx: 363, heightPx: 458 },
		spacesParticles: { zone: 'spaces', cx: 0.5, cy: 0.418, widthPx: 363, heightPx: 458 },
		screensParticles: { zone: 'screens', cx: 0.5, cy: 0.418, widthPx: 363, heightPx: 458 },
		systemsParticles: { zone: 'systems', cx: 0.5, cy: 0.418, widthPx: 363, heightPx: 458 },
		systemsPArea: { zone: 'systems', cx: 0.1, cy: 0.5, widthPx: 900, heightPx: 1000 }
	},

	//iconContainer / systemsContainer alpha fades over drag progress (ex HomeTimeline.tick literals).
	containerFades: {
		iconFadeOutSpan: 0.25, //iconContainer alpha = 1 - progress / span
		systemsFadeInStart: 0.75, //systemsContainer alpha = (progress - start) / span
		systemsFadeInSpan: 0.25
	},

	//Startup reveal delays (ex HomeTimeline._createParticles onParticlesReady setTimeouts).
	revealDelaysMs: {
		sceneStart: 100, //deeplink setup-for-work / intro-scene start
		containerReveal: 500 //home container made visible / alpha 1
	},

	//Viewport width where the screens scene flips to its mobile layout. Runtime-dependent
	//(orientation via the cached window dims, so it only moves on resize), hence a getter.
	//Shared readers (TimelineZones/TimelineItem/TimelineCSSSync) call it live; swap-side
	//HomeScreens keeps its own per-tick cache of it.
	getSwitchScreensLayoutAt(): number {
		return Config.getCurrentMobileSwitch();
	}
};
